/**
 * Copyright (c) [2015-2020] SUSE Linux
 *
 * This software may be modified and distributed under the terms
 * of the MIT license.  See the LICENSE.txt file for details.
 */

import React, { useEffect } from "react";
import { useDispatch } from "react-redux";
import ParticipantActions from "./ParticipantActions";
import { actionCreators as participantsActions } from "../../state/ducks/participants";
import { classNames, attachStream } from "../../utils/common";
import StreamsService from "../../utils/streams-service";
import { User as UserIcon } from "react-feather";

import "./Participant.css";

function setVideo(id, videoRef) {
  const stream = StreamsService.get(id);

  if (stream !== null) {
    console.log("Attaching media stream", id);
    attachStream(videoRef, stream);
  }
}

function toggleFocus(id, focus) {
  return focus === "user"
    ? participantsActions.unsetFocus()
    : participantsActions.setFocus(id);
}

const Video = ({ id, streamReady, isPublisher, isLocalScreen }) => {
  const videoRef = React.createRef();

  useEffect(() => setVideo(id, videoRef.current), [id, videoRef, streamReady]);

  return (
    <video
      ref={videoRef}
      muted={isPublisher}
      autoPlay
      className={classNames(
        isPublisher && !isLocalScreen && "mirrored",
        "max-h-full",
      )}
    />
  );
};

const ParticipantVideo = React.memo(Video);

/**
 * @param props {object}
 * @param props.id {}
 * @param props.username {}
 * @param props.isPublisher {}
 * @param props.isLocalScreen {}
 * @param props.streamReady {}
 * @param props.focus {string?} null: no focus; 'auto': focus because speaking; 'user': focused by clicking
 * @param props.speaking {}
 * @param props.video {}
 */
function Participant({
  id,
  username,
  isPublisher,
  isLocalScreen,
  streamReady,
  focus,
  speaking,
  video,
}) {
  const showVideo = (video || isLocalScreen) && !focus;
  const dispatch = useDispatch();

  return (
    <>
      <div className="flex flex-col items-center">
        <div className={classNames(
          'w-24 h-24 rounded-full bg-gradient-to-r flex items-center justify-center shadow-2xl relative',
          isPublisher && "from-blue-500 to-teal-400",
          !isPublisher && "from-purple-500 to-pink-400",
          speaking && 'speaking-pulse',
          speaking && isPublisher && 'is-publisher'
        )}>
          <span className="text-4xl font-bold text-white uppercase">
            {username.charAt(0)}
          </span>
        </div>
        <div className="mt-4 text-center">
          <div className="text-white text-lg font-medium">{username}</div>
          <div className="text-blue-200 text-sm">{isPublisher ? "（我）" : ""}</div>
        </div>
      </div>
      <div className="absolute">
        <ParticipantVideo
          id={id}
          streamReady={streamReady}
          isLocalScreen={isLocalScreen}
          isPublisher={isPublisher}
        />
      </div>
    </>
  );
}

export default React.memo(Participant);
